import React from 'react';
import Navbar from '../Homepage/Navbar';
import Footer from '../Homepage/Footer';
import NareshSir from '../../assets/team/naresh.jpg';
import useLinks from '../../hooks/useLinks';

const IPD = () => {
    const { pdi_apply_link } = useLinks();

    return (
        <div className="min-h-screen flex flex-col bg-dark-secondary text-gray-100">
            <Navbar />
            <main className="flex-grow pt-28 pb-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                    {/* Header Section */}
                    <div className="text-center mb-16">
                        <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight">
                            In-house Product Development <span className="text-accent-primary">(IPD)</span>
                        </h1>
                        <div className="w-24 h-1 bg-gradient-to-r from-accent-primary to-nvcti-orange mx-auto rounded-full"></div>
                    </div>

                    {/* Introduction & Naresh Sir Section */}
                    <div className="bg-dark-tertiary rounded-3xl p-8 md:p-12 shadow-2xl border border-white/5 mb-16 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-accent-primary/5 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>
                        <div className="flex flex-col items-center gap-12">
                            <div className="w-full">
                                <h2 className="text-3xl font-bold text-accent-primary mb-6">1. Introduction</h2>
                                <p className="text-lg leading-relaxed text-gray-300 mb-6 text-justify">
                                    The <span className="font-semibold text-white">Naresh Vashisht Centre for Tinkering and Innovation (NVCTI)</span> at IIT (ISM) Dhanbad is a central hub for innovation, experimentation, and product-oriented learning.
                                </p>
                                <p className="text-lg leading-relaxed text-gray-300 text-justify">
                                    The <span className="font-semibold text-white">In-house Product Development (IPD)</span> initiative provides a dedicated platform for students and faculty to conceptualize, develop, and prototype technological solutions that address real-world challenges.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Objectives Section */}
                    <div className="mb-16">
                        <h2 className="text-3xl font-bold text-white mb-8 border-l-4 border-nvcti-orange pl-4">2. Objective of IPD</h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            {[
                                "To provide hands-on experience in designing and developing functional products.",
                                "To encourage innovation and technical problem-solving among students.",
                                "To support product-oriented research and foster a culture of making.",
                                "To lay the foundation for entrepreneurship and startup creation."
                            ].map((item, index) => (
                                <div key={index} className="bg-dark-tertiary p-6 rounded-xl border border-white/5 hover:border-accent-primary/30 transition-all hover:-translate-y-1 shadow-lg flex items-start">
                                    <div className="bg-accent-primary/10 p-3 rounded-lg mr-4 shrink-0">
                                        <i className="fas fa-bullseye text-accent-primary text-xl"></i>
                                    </div>
                                    <p className="text-gray-300 text-lg">{item}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Scope Section */}
                    <div className="mb-16">
                        <h2 className="text-3xl font-bold text-white mb-8 border-l-4 border-accent-primary pl-4">3. Scope of IPD</h2>
                        <p className="text-gray-300 mb-8 text-lg">
                            In-house Product Development at NVCTI focuses on fostering innovation across a curated set of technical domains, while remaining open to novel ideas in emerging fields.
                        </p>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[
                                { title: "Electronics & IoT", icon: "fa-microchip", desc: "Design and development of smart systems involving microcontrollers, sensors, actuators, and communication protocols for home automation, environmental monitoring, and industrial control.", color: "text-blue-400" },
                                { title: "Animation & Game Design", icon: "fa-gamepad", desc: "Creation of interactive digital content including 2D/3D animations, educational games, and immersive experiences using AR/VR.", color: "text-purple-400" },
                                { title: "Mechanical & Rapid Prototyping", icon: "fa-cogs", desc: "Engineering of mechanical systems using CAD modeling, 3D printing, laser cutting, and CNC machining for functional testing.", color: "text-orange-400" },
                                { title: "Pouch Battery Cell Assembly", icon: "fa-battery-full", desc: "Design, assembly, and testing of lithium-based pouch cells and battery modules for portable energy storage.", color: "text-green-400" },
                                { title: "Other Fields of Innovation", icon: "fa-lightbulb", desc: "Interdisciplinary ideas including AI/ML integration, biomedical devices, green technologies, space-tech concepts, and smart materials.", color: "text-yellow-400" }
                            ].map((scope, idx) => (
                                <div key={idx} className="bg-dark-tertiary p-6 rounded-xl border border-white/5 hover:border-white/20 transition-all group h-full">
                                    <div className="mb-4">
                                        <i className={`fas ${scope.icon} text-3xl ${scope.color} p-3 bg-white/5 rounded-lg group-hover:scale-110 transition-transform`}></i>
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-3">{scope.title}</h3>
                                    <p className="text-gray-400">{scope.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Key Features Section */}
                    <div className="mb-16 bg-gradient-to-br from-dark-tertiary to-dark-tertiary/50 rounded-3xl p-8 md:p-12 border border-white/5">
                        <h2 className="text-3xl font-bold text-white mb-8 border-l-4 border-nvcti-orange pl-4">4. Key Features</h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[
                                { title: "Idea to Prototype Journey", icon: "fa-road", desc: "Structured support from ideation to working model." },
                                { title: "Technical Mentorship", icon: "fa-chalkboard-teacher", desc: "Domain-specific guidance from faculty, industry experts, and alumni mentors." },
                                { title: "Funding & Resources", icon: "fa-hand-holding-usd", desc: "Access to tools, components, and seed funding for materials and prototyping." },
                                { title: "Skill-building Workshops", icon: "fa-tools", desc: "Training in microcontroller programming, CAD modeling, battery technology, and animation tools." },
                                { title: "IP & Commercialization", icon: "fa-file-contract", desc: "Assistance in filing patents and scaling viable projects toward startup incubation." }
                            ].map((feature, idx) => (
                                <div key={idx} className="flex gap-4">
                                    <div className="mt-1">
                                        <i className={`fas ${feature.icon} text-accent-primary text-2xl`}></i>
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-white mb-1">{feature.title}</h3>
                                        <p className="text-gray-400 leading-snug">{feature.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Process Flow Section */}
                    <div className="mb-10">
                        <h2 className="text-3xl font-bold text-white mb-12 text-center">5. Process Flow of IPD</h2>
                        <div className="relative">
                            {/* Connector Line (Hidden on mobile) */}
                            <div className="hidden md:block absolute top-[28px] left-0 w-full h-1 bg-white/10 z-0"></div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
                                {[
                                    { step: "01", title: "Call for Proposals", desc: "NVCTI issues periodic calls for product ideas." },
                                    { step: "02", title: "Screening & Evaluation", desc: "Projects are reviewed by an expert panel." },
                                    { step: "03", title: "Prototype Development", desc: "Approved teams work in NVCTI labs to build prototypes." },
                                    { step: "04", title: "Review & Iteration", desc: "Regular progress evaluations and feedback sessions." },
                                    { step: "05", title: "Showcasing & Support", desc: "Final prototypes are displayed, with potential for incubation." }
                                ].map((process, idx) => (
                                    <div key={idx} className="relative z-10 flex flex-col items-center text-center">
                                        <div className="w-14 h-14 bg-dark-secondary border-4 border-nvcti-orange rounded-full flex items-center justify-center text-white font-bold text-lg shadow-xl mb-4">
                                            {process.step}
                                        </div>
                                        <h3 className="text-white font-bold text-lg mb-2">{process.title}</h3>
                                        <p className="text-gray-400 text-sm">{process.desc}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="mt-12 text-center">
                            <a
                                href={pdi_apply_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block px-8 py-3 bg-gradient-to-r from-accent-primary to-nvcti-orange text-white font-bold rounded-full shadow-lg hover:scale-105 transition-transform duration-200"
                            >
                                Apply Now <i className="fas fa-arrow-right ml-2"></i>
                            </a>
                        </div>
                    </div>

                </div>
            </main>
            <Footer />
        </div>
    );
};

export default IPD;
